import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, LayoutGrid, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SLIDES } from "@/presentasjon/slides";
import "@/presentasjon/pres.css";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function useFullscreen() {
  const [on, setOn] = useState(() => typeof document !== "undefined" && Boolean(document.fullscreenElement));
  useEffect(() => {
    const h = () => setOn(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggle = useCallback(() => {
    if (!document.fullscreenEnabled) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => null);
  }, []);
  return [on, toggle];
}

/** Skjul kontrollene når musa har vært i ro en stund (som Keynote). */
function useIdle(ms = 2600) {
  const [idle, setIdle] = useState(false);
  const t = useRef(null);
  useEffect(() => {
    const wake = () => {
      setIdle(false);
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => setIdle(true), ms);
    };
    wake();
    window.addEventListener("mousemove", wake);
    window.addEventListener("touchstart", wake, { passive: true });
    window.addEventListener("keydown", wake);
    return () => {
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("touchstart", wake);
      window.removeEventListener("keydown", wake);
      if (t.current) clearTimeout(t.current);
    };
  }, [ms]);
  return idle;
}

/** Miniatyr av en slide – skalerer en 1280×720-«scene» til bredden på kortet. */
function Thumb({ slide }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.25);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / 1280));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="pres-thumb-canvas" aria-hidden="true">
      <div className="pres-thumb-scale" style={{ "--thumb-scale": scale }}>{slide.render()}</div>
    </div>
  );
}

export default function PresentationPage() {
  const { n } = useParams();
  const navigate = useNavigate();
  const total = SLIDES.length;
  const index = clamp((parseInt(n, 10) || 1) - 1, 0, total - 1);
  const [dir, setDir] = useState(1);
  const [overview, setOverview] = useState(false);
  const [fullscreen, toggleFullscreen] = useFullscreen();
  const idle = useIdle();
  const slide = SLIDES[index];
  // Intro-sliden (berre logoen) tel ikkje: forsida er «1 av N»
  const introCount = SLIDES.filter((s) => s.intro).length;
  const shownTotal = total - introCount;
  const shownIndex = index + 1 - introCount; // 0 på intro

  useEffect(() => {
    const prev = document.title;
    document.title = "Kva kan KI gjera for Stord Innkvartering? – Kviteberg AS";
    document.documentElement.classList.add("pres-html");
    return () => {
      document.title = prev;
      document.documentElement.classList.remove("pres-html");
    };
  }, []);

  // Normaliser URL (f.eks. /presentasjon → /presentasjon/1)
  useEffect(() => {
    if (String(index + 1) !== String(n)) navigate(`/presentasjon/${index + 1}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, index]);

  const go = useCallback(
    (to) => {
      const next = clamp(to, 0, total - 1);
      if (next === index) return;
      setDir(next > index ? 1 : -1);
      navigate(`/presentasjon/${next + 1}`);
    },
    [index, total, navigate]
  );
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
        case "PageDown":
        case " ":
        case "Enter":
          e.preventDefault();
          if (overview) setOverview(false);
          else next();
          break;
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp":
        case "Backspace":
          e.preventDefault();
          prev();
          break;
        case "Home":
          go(0);
          break;
        case "End":
          go(total - 1);
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
        case "o":
        case "O":
          setOverview((v) => !v);
          break;
        case "Escape":
          setOverview((v) => !v);
          break;
        default:
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go, total, toggleFullscreen, overview]);

  // Sveip på touch
  const touch = useRef(null);
  const onPointerDown = (e) => {
    // Inne i en modal/dialog skal verken sveip eller klikk bla
    if (e.target instanceof Element && e.target.closest("[data-pres-hold]")) { touch.current = null; return; }
    touch.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  };
  const onPointerUp = (e) => {
    const s = touch.current;
    touch.current = null;
    if (!s) return;
    if (e.target instanceof Element && e.target.closest("[data-pres-hold]")) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next();
      else prev();
      return;
    }
    // Klikk: høyre 2/3 → neste, venstre 1/3 → tilbake (bare om det ikke var et trykk på en knapp)
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8 && Date.now() - s.t < 500 && !(e.target instanceof Element && e.target.closest("button, a, kbd, [data-pres-hold]"))) {
      if (e.clientX < window.innerWidth / 3) prev();
      else next();
    }
  };

  // Overgang: den nye sliden glir inn (lett skalert) mens den gamle toner ut – bare transform/opacity
  const variants = useMemo(
    () => ({
      enter: (d) => ({ opacity: 0, y: d > 0 ? 28 : -28, scale: 0.985 }),
      center: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.46, ease: [0.22, 1, 0.36, 1] } },
      exit: (d) => ({ opacity: 0, y: d > 0 ? -18 : 18, scale: 0.995, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }),
    }),
    []
  );

  return (
    <div className="pres" data-testid="pres-page" data-slide={slide.id} data-index={index + 1} data-idle={idle || undefined}>
      <div className="pres-stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} role="presentation">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          {/* Slider med samme `scene` deler nøkkel: seksjonen blir ikke remontert, bare oppdatert – figuren står stille mens lagene kommer til */}
          <motion.section
            key={slide.scene || slide.id}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="pres-slide"
            aria-roledescription="slide"
            aria-label={slide.intro ? slide.title : `${shownIndex} av ${shownTotal}: ${slide.title}`}
          >
            {slide.render()}
          </motion.section>
        </AnimatePresence>
      </div>

      {/* Framdrift */}
      <div className="pres-progress" aria-hidden="true" data-testid="pres-progress">
        <span style={{ transform: `scaleX(${Math.max(0, shownIndex) / shownTotal})` }} />
      </div>

      {/* Kontroller */}
      <div className={cn("pres-controls", idle && !overview && "pres-controls-hidden")} data-testid="pres-controls">
        <button type="button" onClick={prev} disabled={index === 0} className="pres-btn" aria-label="Forrige slide" data-testid="pres-prev">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="pres-counter tabular-nums" data-testid="pres-counter">
          {shownIndex > 0 ? <>{shownIndex} <span className="opacity-50">/ {shownTotal}</span></> : <span className="opacity-50">Start</span>}
        </span>
        <button type="button" onClick={next} disabled={index === total - 1} className="pres-btn" aria-label="Neste slide" data-testid="pres-next">
          <ChevronRight className="h-4 w-4" />
        </button>
        <span className="pres-sep" />
        <button type="button" onClick={() => setOverview((v) => !v)} className="pres-btn" aria-label="Oversikt (O)" title="Oversikt (O)" data-testid="pres-overview-toggle">
          <LayoutGrid className="h-4 w-4" />
        </button>
        <button type="button" onClick={toggleFullscreen} className="pres-btn" aria-label={fullscreen ? "Avslutt fullskjerm (F)" : "Fullskjerm (F)"} title="Fullskjerm (F)" data-testid="pres-fullscreen">
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>

      {/* Oversikt */}
      <AnimatePresence>
        {overview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} className="pres-overview" data-testid="pres-overview">
            <div className="flex items-center justify-between px-6 pt-5 sm:px-10">
              <div className="pres-eyebrow">Oversikt · {shownTotal} {shownTotal === 1 ? "slide" : "slides"}</div>
              <button type="button" onClick={() => setOverview(false)} className="pres-btn" aria-label="Lukk oversikt" data-testid="pres-overview-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-4 p-6 sm:grid-cols-2 sm:p-10 lg:grid-cols-3">
              {SLIDES.map((s, i) => (
                // <div role="button"> (ikke <button>): miniatyrene kan inneholde ekte app-komponenter med egne knapper
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setOverview(false); go(i); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOverview(false); go(i); } }}
                  className={cn("pres-thumb", i === index && "pres-thumb-active")}
                  data-testid="pres-overview-item"
                  data-slide={s.id}
                >
                  <Thumb slide={s} />
                  <div className="flex items-center justify-between px-3 py-2 text-[13px]">
                    <span className="font-semibold">{s.title}</span>
                    <span className="tabular-nums text-[var(--pres-muted)]">{s.intro ? "–" : i + 1 - introCount}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
